import { can } from '../../auth/types';
import { decimalScale, decimalToUnits, sumDecimal, unitsToDecimal } from '../../components/data-grid/money';
import type { DocumentStatus } from '../documents/filters';

/**
 * Pure rules of the generic document form: the balance shown next to the
 * lines, which accounts may be picked and which operations the current user
 * may see. The view only renders what these functions decide.
 */

export type FieldType =
  | 'string'
  | 'text'
  | 'number'
  | 'amount'
  | 'quantity'
  | 'date'
  | 'boolean'
  | 'catalog'
  | 'enum';

export interface FieldDefinitionView {
  name: string;
  type: FieldType;
  required?: boolean;
  options?: string[];
}

export interface StatusTransitionView {
  from: DocumentStatus;
  to: DocumentStatus;
  operation: 'create' | 'edit' | 'approve' | 'post' | 'unpost' | 'cancel';
}

export interface DocumentTypeView {
  code: string;
  name: string;
  fields: FieldDefinitionView[];
  tableFields: {
    fields: FieldDefinitionView[];
    maxRows?: number;
  };
  statusModel: {
    approvalEnabled: boolean;
    transitions: StatusTransitionView[];
  };
  operations: Array<{ code: string; name: string }>;
}

export interface FormLine {
  key: string;
  lineNo: number;
  data: Record<string, unknown>;
  amount: string;
  dimensions: Record<string, string>;
}

export interface DocumentFormState {
  id?: string;
  type: string;
  operationKind: string;
  number: string;
  date: string;
  status: DocumentStatus;
  data: Record<string, unknown>;
  lines: FormLine[];
}

export interface AccountDimensionRuleView {
  slot: number;
  dimType: string;
  required?: boolean;
}

export interface AccountOption {
  id: string;
  code: string;
  name: string;
  isGroup: boolean;
  isOffbalance: boolean;
  dimensionRules: AccountDimensionRuleView[];
}

export interface Balance {
  debit: string;
  credit: string;
  difference: string;
  balanced: boolean;
}

export type OperationName = 'save' | 'approve' | 'post' | 'cancel';

const EDITABLE_STATUSES: DocumentStatus[] = ['draft', 'pending', 'approved'];

function field(line: FormLine, name: string): string {
  const value = line.data[name];
  return typeof value === 'string' ? value : '';
}

/**
 * A line either names one side explicitly (`side: D|C` — the opening-balance
 * shape) or a debit and a credit account (the manual-entry shape). The second
 * adds the same amount to both sides. Anything else counts on the debit side,
 * so an incomplete line can never look balanced.
 */
export function computeBalance(lines: readonly FormLine[]): Balance {
  const debit: string[] = [];
  const credit: string[] = [];
  for (const line of lines) {
    const side = field(line, 'side').toUpperCase();
    if (side === 'D') {
      debit.push(line.amount);
      continue;
    }
    if (side === 'C') {
      credit.push(line.amount);
      continue;
    }
    if (field(line, 'debitAccount') !== '' || field(line, 'creditAccount') !== '') {
      debit.push(line.amount);
      credit.push(line.amount);
      continue;
    }
    debit.push(line.amount);
  }
  const debitTotal = sumDecimal(debit);
  const creditTotal = sumDecimal(credit);
  const scale = Math.max(decimalScale(debitTotal), decimalScale(creditTotal), 4);
  const difference = unitsToDecimal(
    decimalToUnits(debitTotal, scale) - decimalToUnits(creditTotal, scale),
    scale,
  );
  return {
    debit: debitTotal,
    credit: creditTotal,
    difference,
    balanced: decimalToUnits(difference, scale) === 0n,
  };
}

export function leafAccounts(accounts: readonly AccountOption[]): AccountOption[] {
  return accounts.filter((account) => !account.isGroup);
}

export function accountLabel(account: AccountOption): string {
  return `${account.code} — ${account.name}`;
}

/** Dimension pickers follow the debit account's policy; the credit account does not widen it. */
export function dimensionRulesFor(line: FormLine, accounts: readonly AccountOption[]): AccountDimensionRuleView[] {
  const debitCode = field(line, 'debitAccount');
  const creditCode = field(line, 'creditAccount');
  const account =
    accounts.find((candidate) => candidate.code === debitCode) ??
    accounts.find((candidate) => candidate.code === creditCode);
  if (account === undefined) {
    return [];
  }
  const seen = new Set<number>();
  const rules: AccountDimensionRuleView[] = [];
  for (const rule of [...account.dimensionRules].sort((left, right) => left.slot - right.slot)) {
    if (seen.has(rule.slot)) {
      continue;
    }
    seen.add(rule.slot);
    rules.push(rule);
  }
  return rules;
}

export function transitionFor(
  type: DocumentTypeView,
  status: DocumentStatus,
  operation: 'approve' | 'post' | 'cancel',
): StatusTransitionView | undefined {
  return type.statusModel.transitions.find(
    (transition) => transition.from === status && transition.operation === operation,
  );
}

const OPERATION_PERMISSION: Record<OperationName, string> = {
  save: 'core.document.edit',
  approve: 'core.document.approve',
  post: 'core.document.post',
  cancel: 'core.document.cancel',
};

export function availableOperations(
  type: DocumentTypeView,
  status: DocumentStatus,
  permissions: readonly string[],
  isNew = false,
): Set<OperationName> {
  const operations = new Set<OperationName>();
  // `create` opens a form the role may fill in; editing an existing document is
  // `edit` only, otherwise a create-only role gets a save button the server refuses.
  const mayEdit = can(permissions, OPERATION_PERMISSION.save) || (isNew && can(permissions, 'core.document.create'));
  if (mayEdit && EDITABLE_STATUSES.includes(status)) {
    operations.add('save');
  }
  if (can(permissions, OPERATION_PERMISSION.approve) && transitionFor(type, status, 'approve') !== undefined) {
    operations.add('approve');
  }
  if (can(permissions, OPERATION_PERMISSION.post) && transitionFor(type, status, 'post') !== undefined) {
    operations.add('post');
  }
  if (can(permissions, OPERATION_PERMISSION.cancel) && transitionFor(type, status, 'cancel') !== undefined) {
    operations.add('cancel');
  }
  return operations;
}

export function statusKeyOf(status: DocumentStatus): string {
  return `documentFormStatus${status.charAt(0).toUpperCase()}${status.slice(1)}`;
}
